import { useCallback, useMemo } from 'react';
import noop from 'lodash/noop';
import type {
	AccessRole,
	ViewAccessLevel,
} from '@jira/polaris__domain-view/src/ViewAccessLevel.types.tsx';
import { useEnvironmentContainer } from '@jira/polaris__component-environment-container/src/useEnvironmentContainer.tsx';
import { useIsViewPermissionsEnabled } from '@jira/polaris__lib-entitlement-utils/src/index.tsx';
import { useIsAdmin as useIsTenantContextAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin as useIsTenantContextSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	type Action,
	type BoundActions,
	type HookFunction,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { PermissionsStore, usePermissions, usePermissionActions } from './PermissionsContainer.tsx';
import type { SupportedPermissions } from './SupportedPermissions.tsx';
import { useHasPermissions } from './useHasPermissions.tsx';
import type { LoadViewPermissionsForCurrentUserState } from './loadViewPermissionsForCurrentUser.tsx';
import type { State, Props } from './State.types.tsx';
import { useGetHasPermissionCallback } from './useGetHasPermissionCallback.tsx';

type ProjectId = string;

/**
 * @deprecated use useHasPermissions instead
 */
const COLLECTION_VIEW_FORBIDDEN_PERMISSION_KEYS = ['CREATE_ISSUES', 'ADMINISTER_PROJECTS'];

// if ommitted, Permission is true for all projects
type Options =
	| {
			// Permission is true for a specific project
			projectId?: ProjectId;
	  }
	| {
			// Permission is true for a specific projects
			projectIds?: ProjectId[];
	  }
	| {
			// Permission is true for at least one project (in multi project context)
			anyProject?: boolean;
	  };

/**
 * Do not use this hook directly. Exported for testing only.
 * @internal
 * @private
 */
export const useIsEmbeddedView: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			readonly loadPermissions: () => Action<State, Props>;
			readonly switchPreviewContributorMode: (isPreviewOn: boolean) => Action<State, Props>;
			readonly setContributorCreateIssuePermission: (enabled: boolean) => Action<State, Props>;
			readonly loadViewPermissionsForCurrentUser: (containerId: string) => Action<
				State,
				Props,
				Promise<{
					role: AccessRole | undefined;
					accessLevel: ViewAccessLevel | undefined;
					state: LoadViewPermissionsForCurrentUserState;
				}>
			>;
		}
	>,
	void
> = createHook(PermissionsStore, {
	selector: (state) => state.meta.loadingProps?.isEmbeddedView || false,
});

/**
 * Do not use this hook directly. Exported for testing only.
 * @internal
 * @private
 */
export const useIsSharedView: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			readonly loadPermissions: () => Action<State, Props>;
			readonly switchPreviewContributorMode: (isPreviewOn: boolean) => Action<State, Props>;
			readonly setContributorCreateIssuePermission: (enabled: boolean) => Action<State, Props>;
			readonly loadViewPermissionsForCurrentUser: (containerId: string) => Action<
				State,
				Props,
				Promise<{
					role: AccessRole | undefined;
					accessLevel: ViewAccessLevel | undefined;
					state: LoadViewPermissionsForCurrentUserState;
				}>
			>;
		}
	>,
	void
> = createHook(PermissionsStore, {
	selector: (state) => state.meta.loadingProps?.isSharedView || false,
});

/**
 * Do not use this hook directly. Exported for testing only.
 * @internal
 * @private
 */
export const useIsCollectionView: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			readonly loadPermissions: () => Action<State, Props>;
			readonly switchPreviewContributorMode: (isPreviewOn: boolean) => Action<State, Props>;
			readonly setContributorCreateIssuePermission: (enabled: boolean) => Action<State, Props>;
			readonly loadViewPermissionsForCurrentUser: (containerId: string) => Action<
				State,
				Props,
				Promise<{
					role: AccessRole | undefined;
					accessLevel: ViewAccessLevel | undefined;
					state: LoadViewPermissionsForCurrentUserState;
				}>
			>;
		}
	>,
	void
> = createHook(PermissionsStore, {
	selector: (state) => state.meta.loadingProps?.isCollectionView || false,
});

const useIsGlobalViewSettings: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			readonly loadPermissions: () => Action<State, Props>;
			readonly switchPreviewContributorMode: (isPreviewOn: boolean) => Action<State, Props>;
			readonly setContributorCreateIssuePermission: (enabled: boolean) => Action<State, Props>;
			readonly loadViewPermissionsForCurrentUser: (containerId: string) => Action<
				State,
				Props,
				Promise<{
					role: AccessRole | undefined;
					accessLevel: ViewAccessLevel | undefined;
					state: LoadViewPermissionsForCurrentUserState;
				}>
			>;
		}
	>,
	void
> = createHook(PermissionsStore, {
	selector: (state) => state.meta.loadingProps?.isGlobalViewSettings || false,
});

/**
 * Creates a hook that returns the permission for the given permission key.
 */
export const createPermissionHook =
	(permissionKey: SupportedPermissions) =>
	(options?: Options): boolean[] => {
		const definedOptions = options ?? {};
		const projectIdInArray =
			'projectId' in definedOptions && definedOptions.projectId
				? [definedOptions.projectId]
				: undefined;
		const projectIds =
			'projectIds' in definedOptions && definedOptions.projectIds
				? definedOptions.projectIds
				: undefined;

		const projectMatch =
			'anyProject' in definedOptions && definedOptions.anyProject === true ? 'any' : 'all';

		const result = useHasPermissions({
			permissionKeys: [permissionKey],
			projectIds: projectIdInArray ?? projectIds,
			projectMatch,
		});

		return [result];
	};

/**
 * @deprecated use src/packages/polaris/component-permissions-store/src/controllers/permissions/hooks/useIsPreviewContributorMode.tsx
 */
const useIsInContributorPreviewMode = createHook(PermissionsStore, {
	selector: (state) => state.isPreviewContributorMode,
});

export const useIsSiteAdmin = (): boolean => {
	const isSiteAdmin = useIsTenantContextSiteAdmin();
	const [isInContributorPreviewMode] = useIsInContributorPreviewMode();
	return isSiteAdmin && !isInContributorPreviewMode;
};

export const useIsAdmin = (): boolean => {
	const isAdmin = useIsTenantContextAdmin();
	const [isInContributorPreviewMode] = useIsInContributorPreviewMode();
	return isAdmin && !isInContributorPreviewMode;
};

export const useIsLoadingPermissions: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			readonly loadPermissions: () => Action<State, Props>;
			readonly switchPreviewContributorMode: (isPreviewOn: boolean) => Action<State, Props>;
			readonly setContributorCreateIssuePermission: (enabled: boolean) => Action<State, Props>;
			readonly loadViewPermissionsForCurrentUser: (containerId: string) => Action<
				State,
				Props,
				Promise<{
					role: AccessRole | undefined;
					accessLevel: ViewAccessLevel | undefined;
					state: LoadViewPermissionsForCurrentUserState;
				}>
			>;
		}
	>,
	void
> = createHook(PermissionsStore, {
	selector: (state) => state.meta.loading,
});

export const useIsLoadedPermissions: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			readonly loadPermissions: () => Action<State, Props>;
			readonly switchPreviewContributorMode: (isPreviewOn: boolean) => Action<State, Props>;
			readonly setContributorCreateIssuePermission: (enabled: boolean) => Action<State, Props>;
			readonly loadViewPermissionsForCurrentUser: (containerId: string) => Action<
				State,
				Props,
				Promise<{
					role: AccessRole | undefined;
					accessLevel: ViewAccessLevel | undefined;
					state: LoadViewPermissionsForCurrentUserState;
				}>
			>;
		}
	>,
	void
> = createHook(PermissionsStore, {
	selector: (state) => state.meta.loaded,
});

const useProjectPermissionsLength = createHook(PermissionsStore, {
	selector: (state, projectId?: ProjectId) => {
		return projectId
			? (state.permissionsByProjectId[projectId]?.length ?? 0)
			: Object.values(state.permissionsByProjectId).flat().length;
	},
});

export const useCanAdministerProject: (options?: Options) => boolean[] =
	createPermissionHook('ADMINISTER_PROJECTS');

export const useCanCreateIssues: (options?: Options) => boolean[] =
	createPermissionHook('CREATE_ISSUES');
export const useCanEditIssues: (options?: Options) => boolean[] =
	createPermissionHook('EDIT_ISSUES');
export const useCanDeleteIssues: (options?: Options) => boolean[] =
	createPermissionHook('DELETE_ISSUES');
export const useCanMoveIssues: (options?: Options) => boolean[] =
	createPermissionHook('MOVE_ISSUES');
export const useCanAddComments: (options?: Options) => boolean[] =
	createPermissionHook('ADD_COMMENTS');
export const useHasEditOwnCommentsPermission: (options?: Options) => boolean[] =
	createPermissionHook('EDIT_OWN_COMMENTS');
export const useHasEditAllCommentsPermission: (options?: Options) => boolean[] =
	createPermissionHook('EDIT_ALL_COMMENTS');
export const useCanModifyReporter: (options?: Options) => boolean[] =
	createPermissionHook('MODIFY_REPORTER');

export const useCanManageWatchers: (options?: Options) => boolean[] =
	createPermissionHook('MANAGE_WATCHERS');
export const useCanViewWatchers: (options?: Options) => boolean[] = createPermissionHook(
	'VIEW_VOTERS_AND_WATCHERS',
);

export const useCanAddAttachments: (options?: Options) => boolean[] =
	createPermissionHook('CREATE_ATTACHMENTS');
export const useCanDeleteOwnAttachments: (options?: Options) => boolean[] =
	createPermissionHook('DELETE_OWN_ATTACHMENTS');
export const useCanDeleteAllAttachments: (options?: Options) => boolean[] =
	createPermissionHook('DELETE_ALL_ATTACHMENTS');
export const useCanMakeBulkChanges: (options?: Options) => boolean[] =
	createPermissionHook('BULK_CHANGE');

const useCanCreateProjectPermission = createPermissionHook('CREATE_PROJECT');
const useHasLinkIssuesPermission = createPermissionHook('LINK_ISSUES');
const useCanScheduleIssues = createPermissionHook('SCHEDULE_ISSUES');
const useHasManageDeliveryPermission = createPermissionHook('MANAGE_DISCOVERY_DELIVERY');
const useHasMergeIdeasPermission = createPermissionHook('MERGE_DISCOVERY_ISSUES');
const useHasArchiveIdeasPermission = createPermissionHook('ARCHIVE_DISCOVERY_ISSUES');
const useHasVotePermission = createPermissionHook('DISCOVERY_VOTE');
const useHasCreateInsightsPermission = createPermissionHook('CREATE_DISCOVERY_INSIGHTS');
const useHasManageInsightsPermission = createPermissionHook('MANAGE_DISCOVERY_INSIGHTS');

const useHasManageViewsPermission = createPermissionHook('MANAGE_DISCOVERY_VIEWS');

const useHasPublishDiscoveryViewsPermission = createPermissionHook('PUBLISH_DISCOVERY_VIEWS');
const useHasPublishPublicDiscoveryViewsPermission = createPermissionHook(
	'PUBLISH_PUBLIC_DISCOVERY_VIEWS',
);

const useHasExportViewsPermission = createPermissionHook('SHARE_DISCOVERY_VIEWS');
const useHasCreateViewCommentsPermission = createPermissionHook('CREATE_DISCOVERY_VIEW_COMMENTS');
const useHasManageViewCommentsPermission = createPermissionHook('MANAGE_DISCOVERY_VIEW_COMMENTS');
const useHasManageIdeaTemplatesPermission = createPermissionHook('MANAGE_DISCOVERY_IDEA_TEMPLATES');
const useHasPinIssueViewFieldsPermission = createPermissionHook('PIN_DISCOVERY_ISSUE_VIEW_FIELDS');

export const useCanCreateAndEditIssues = (options?: Options): boolean => {
	const [canCreateIssues] = useCanCreateIssues(options);
	const [canEditIssues] = useCanEditIssues(options);
	return canCreateIssues && canEditIssues;
};

export const useCanLinkIssues = (options?: Options): boolean => {
	const [hasLinkIssuesPermission] = useHasLinkIssuesPermission(options);
	const [canEditIssues] = useCanEditIssues(options);
	return hasLinkIssuesPermission && canEditIssues;
};

export const useCanConnectIssues = (options?: Options): boolean => {
	const canLinkIssues = useCanLinkIssues(options);
	return canLinkIssues;
};

const CAN_EDIT_ISSUE_PERMISSIONS: SupportedPermissions[] = ['EDIT_ISSUES'];
export const useCanEditIssueByProject: () => (projectId: string | undefined) => boolean = () =>
	useGetHasPermissionCallback(CAN_EDIT_ISSUE_PERMISSIONS);

const CAN_CONNECT_ISSUES_PERMISSIONS: SupportedPermissions[] = ['EDIT_ISSUES', 'LINK_ISSUES'];
export const useCanConnectIssuesByProject: () => (projectId: string | undefined) => boolean = () =>
	useGetHasPermissionCallback(CAN_CONNECT_ISSUES_PERMISSIONS);

export const useCanPinIssueViewFields = (options?: Options): boolean => {
	const [hasPinIsssueViewFieldsPermission] = useHasPinIssueViewFieldsPermission(options);
	const [isCollectionView] = useIsCollectionView();
	return !isCollectionView && hasPinIsssueViewFieldsPermission;
};

export const useCanEditOwnComments = (options?: Options): boolean => {
	const [hasEditOwnCommentsPermission] = useHasEditOwnCommentsPermission(options);
	return hasEditOwnCommentsPermission;
};

export const useCanEditAllComments = (options?: Options): boolean => {
	const [hasEditAllCommentsPermission] = useHasEditAllCommentsPermission(options);
	return hasEditAllCommentsPermission;
};

export const useCanAddAndEditOwnViewComments = (options?: Options): boolean => {
	const [hasCreateViewComments] = useHasCreateViewCommentsPermission(options);
	return hasCreateViewComments;
};

export const useCanEditAllViewComments = (options?: Options): boolean => {
	const [hasManageViewCommentsPermission] = useHasManageViewCommentsPermission(options);
	return hasManageViewCommentsPermission;
};

export const useCanRankIdeas = (options?: Options): boolean => {
	const [canEditIssues] = useCanEditIssues(options);
	const [canScheduleIssues] = useCanScheduleIssues(options);
	return canEditIssues && canScheduleIssues;
};

export const useCanManageGlobalFields = (): boolean => {
	const isAdmin = useIsAdmin();
	return isAdmin;
};

export const useCanImportIssues = (options?: Options): boolean => {
	const [canEditIssues] = useCanEditIssues(options);
	const [canMakeBulkChanges] = useCanMakeBulkChanges(options);
	return canEditIssues && canMakeBulkChanges;
};

export const useCanManageIdeaTemplates = (options?: Options): boolean => {
	const [hasManageDescriptionTemplatesPermission] = useHasManageIdeaTemplatesPermission(options);
	return hasManageDescriptionTemplatesPermission;
};

export const useCanAddPlayContributions = (options?: Options): boolean => {
	const [hasVotePermissions] = useHasVotePermission(options);
	return hasVotePermissions;
};

export const useCanCreateProject = (): boolean => {
	const [canCreateProject] = useCanCreateProjectPermission({});
	return canCreateProject;
};

/**
 * returns permissions related to managing a view (edit, see, access level)
 * @param containerId - view UUID or collection container ID
 * @returns
 */
export const useManageViewPermissions = (
	containerId: string | undefined,
	options?: Options,
):
	| {
			canManageView: boolean;
			canManageViews: boolean;
			canSeeView: boolean;
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			loadViewPermissionsForCurrentUser: (...args: any[]) => void;
			viewAccessLevel?: undefined;
	  }
	| {
			canManageView: boolean;
			canManageViews: boolean;
			canSeeView: boolean;
			viewAccessLevel: ViewAccessLevel | undefined;
			loadViewPermissionsForCurrentUser: (containerId: string) => Promise<{
				role: AccessRole | undefined;
				accessLevel: ViewAccessLevel | undefined;
				state: LoadViewPermissionsForCurrentUserState;
			}>;
	  } => {
	const isViewPermissionsEnabled = useIsViewPermissionsEnabled();
	const [canManageViews] = useHasManageViewsPermission(options);
	const { loadViewPermissionsForCurrentUser } = usePermissionActions();
	const [state] = usePermissions();

	if (!isViewPermissionsEnabled) {
		return {
			canManageView: canManageViews,
			canManageViews,
			canSeeView: true,
			loadViewPermissionsForCurrentUser: noop,
		};
	}

	if (!containerId) {
		return {
			canManageView: false,
			canManageViews,
			canSeeView: true,
			loadViewPermissionsForCurrentUser,
		};
	}

	const permissionsForThisView = state.viewPermissions[containerId];

	const canManageView = canManageViews && Boolean(permissionsForThisView?.data?.canEdit);

	return {
		canManageView,
		canManageViews,
		canSeeView:
			permissionsForThisView?.loading ||
			permissionsForThisView?.data === undefined ||
			Boolean(permissionsForThisView?.data.canView),
		viewAccessLevel: permissionsForThisView?.data?.accessLevel,
		loadViewPermissionsForCurrentUser,
	};
};

export const useCanEditCollection = ({
	collectionUUID,
}: {
	collectionUUID: string | undefined;
}): boolean => {
	const { hasProductDiscoveryAccess: isLicensedUser } = useApplicationPermissions() ?? {};

	const { canManageView } = useManageViewPermissions(collectionUUID);

	return isLicensedUser && canManageView;
};

export const useLoadCollectionPermissions = (
	collectionUUID: string | undefined,
	// oxlint-disable-next-line typescript/no-invalid-void-type
): (() => void | Promise<{
	role: AccessRole | undefined;
	accessLevel: ViewAccessLevel | undefined;
	state: LoadViewPermissionsForCurrentUserState;
}>) => {
	const { loadViewPermissionsForCurrentUser } = useManageViewPermissions(collectionUUID);

	return useCallback(() => {
		if (collectionUUID) {
			return loadViewPermissionsForCurrentUser(collectionUUID);
		}
	}, [collectionUUID, loadViewPermissionsForCurrentUser]);
};

export const useCanManageViews = (options?: Options): boolean => {
	const [hasManageViewsPermission] = useHasManageViewsPermission(options);
	const [isCollectionView] = useIsCollectionView();
	const [isGlobalViewSettings] = useIsGlobalViewSettings();
	return (
		hasManageViewsPermission ||
		// temporarily give everyone manageViews permissions in a collection view
		// TODO: https://pi-dev-sandbox.atlassian.net/browse/POL-17942
		isCollectionView ||
		// On global view settings, there's no view level permissions:
		// all users who can access the global view settings, can create and edit global views
		(isGlobalViewSettings && fg('polaris_global_views'))
	);
};

export const useCanMergeIdeas = (options?: Options): boolean => {
	const [hasMergeIdeasPermission] = useHasMergeIdeasPermission(options);
	return hasMergeIdeasPermission;
};

export const useCanArchiveIdeas = (options?: Options): boolean => {
	const [hasArchiveIdeasPermission] = useHasArchiveIdeasPermission(options);
	return hasArchiveIdeasPermission;
};

export const useCanPublishDiscoveryViews = (options?: Options): boolean =>
	useHasPublishDiscoveryViewsPermission(options)[0];

export const useCanPublishPublicDiscoveryViews = (options?: Options): boolean =>
	useHasPublishPublicDiscoveryViewsPermission(options)[0];

export const useCanExportViews = (options?: Options): boolean => {
	const [hasExportViewsPermission] = useHasExportViewsPermission(options);
	const collectionId = useEnvironmentContainer()?.id ?? '';
	const [isCollectionView] = useIsCollectionView();

	const canEditCollection = useCanEditCollection({
		collectionUUID: collectionId,
	});

	return (isCollectionView && canEditCollection) || hasExportViewsPermission;
};

export const useCanManageInsights = (options?: Options): boolean => {
	const [hasManageInsightsPermission] = useHasManageInsightsPermission(options);
	return hasManageInsightsPermission;
};

export const useCanCreateInsights = (options?: Options): boolean => {
	const [hasCreateInsightsPermission] = useHasCreateInsightsPermission(options);
	return hasCreateInsightsPermission;
};

export const useCanManageDeliveryTickets = (options?: Options): boolean => {
	const [hasLinkIssuesPermission] = useHasLinkIssuesPermission(options);
	const [hasManageDeliveryPermission] = useHasManageDeliveryPermission(options);
	return hasManageDeliveryPermission && hasLinkIssuesPermission;
};

export const useCanEditDeliveryTableSettings = (options?: Options): boolean => {
	const [hasManageDeliveryPermission] = useHasManageDeliveryPermission(options);
	return hasManageDeliveryPermission;
};

export const useHasNoProjectPermissions = (projectId?: ProjectId): [boolean] => {
	const [isCollectionView] = useIsCollectionView();
	const [projectPermissionsLength] = useProjectPermissionsLength(projectId);
	return [projectPermissionsLength === 0 && !isCollectionView];
};

export const useCanEditGroupsRollup = (options?: Options): boolean => {
	const [canEditIssues] = useCanEditIssues(options);
	const [isCollectionView] = useIsCollectionView();

	// TODO: should be only allowed for view owner in case of a collection view
	return isCollectionView || canEditIssues;
};

/**
 * Returns whether the user can view comment reactions.
 * The intention is to create a dedicated VIEW_COMMENT_REACTIONS permission in the very near future.
 * For now, we use the jiv-22186-is-reactions-disabled feature gate to determine visibility -
 * when the gate is true, reactions are disabled and this returns false.
 */
export const useCanViewCommentReactions = (_options?: Options): boolean[] => {
	return [!fg('jiv-22186-is-reactions-disabled')];
};

/**
 * @deprecated this uses COLLECTION_VIEW_FORBIDDEN_PERMISSION_KEYS, should be removed in the future in favour of useHasPermissions
 */
export const usePerProjectPermissionMap = (
	permissionKey: SupportedPermissions,
): Record<string, boolean> => {
	const [state] = usePermissions();
	const [isCollectionView] = useIsCollectionView();

	return useMemo(() => {
		const forbidden =
			isCollectionView && COLLECTION_VIEW_FORBIDDEN_PERMISSION_KEYS.includes(permissionKey);
		const result: Record<string, boolean> = {};

		if (forbidden) {
			Object.keys(state.permissionsByProjectId).forEach((id) => {
				result[id] = false;
			});
			return result;
		}

		Object.entries(state.permissionsByProjectId).forEach(([id, permissions]) => {
			const permission = permissions?.find(({ key }) => key === permissionKey)?.havePermission;
			result[id] = Boolean(permission);
		});
		return result;
	}, [state.permissionsByProjectId, permissionKey, isCollectionView]);
};
